/**
 * Aero Users — in-app admin user-management surface (Aero only).
 *
 * Structurally modelled on AeroSites (search box + client-side filtering +
 * mapped list) and styled with the flat .ut-theme classes and --aero-* tokens
 * per CLAUDE.md. It is SEPARATE from the Figmafolio stats dashboard at /admin,
 * which is untouched; this page reuses only that dashboard's read LOGIC (the
 * shared userDetailReads helper), never its Bootstrap styling.
 *
 * List: live-loads the whole user collection once (db.collection('user').get())
 * and shows ONLY fields that live on the user doc itself (name, email,
 * signUpDate, signUpMethod, signUpProduct, lastActiveAt). No per-user
 * subcollection reads at list time — that would reintroduce the ~4,900x
 * fan-out the nightly rollup exists to avoid. ~4,900 rows are rendered through
 * a capped visible window (Load more) so the DOM stays light.
 *
 * Detail (click a row): live-fetches that user's plan (from subscriptions),
 * their sites, and their user tests + sessions via the shared helper. Sessions
 * deep-link into /session-review exactly like AdminDashboard. This detail view
 * is where the admin controls (plan override, share-all-sites) live.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useOutletContext, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getFunctions, httpsCallable } from 'firebase/functions';
import firebase, { auth } from '../../firebase';
import { GoogleGLogo } from '../../components/GoogleSignInButton/GoogleSignInButton';
import { buildSiteUrl } from '../../utils/domainUtils';
import { inferBrand } from '../AdminDashboard/brandInference';
import { readUserItems, readUserPlan } from '../AdminDashboard/userDetailReads';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroUsers.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Parse a Firestore Timestamp / {seconds} / JS Date / string to a JS Date, or
// null when missing/unparseable.
function toJsDate(v) {
  if (!v) return null;
  let d;
  if (typeof v.toDate === 'function') d = v.toDate();
  else if (v.seconds !== undefined) d = new Date(v.seconds * 1000);
  else d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// "18 Mar" in the current year, "18 Mar 2025" otherwise. Dash when missing.
function fmtFriendlyDate(v) {
  const d = toJsDate(v);
  if (!d) return '—';
  const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === new Date().getFullYear() ? label : `${label} ${d.getFullYear()}`;
}

// Smart relative time for the last-active column.
function fmtRelative(v) {
  const d = toJsDate(v);
  if (!d) return '—';
  const min = 60000;
  const hour = 60 * min;
  const day = 24 * hour;
  const diff = Date.now() - d.getTime();
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 14 * day) return `${Math.floor(diff / day)}d ago`;
  return fmtFriendlyDate(d);
}

function sortTime(v) {
  const d = toJsDate(v);
  return d ? d.getTime() : null;
}

// Plan → flat tag variant. Free is the neutral tag; paid tiers get colour.
function planTagClass(plan) {
  switch (plan) {
    case 'Pro': return 'aero-tag aero-tag-green';
    case 'Solo':
    case 'Basic': return 'aero-tag aero-tag-blue';
    case 'Trial': return 'aero-tag aero-tag-amber';
    default: return 'aero-tag';
  }
}

// The plan-override keys an admin may set (mirrors PLAN_OVERRIDE_KEYS in
// functions/index.js). Display labels for the buttons.
const OVERRIDE_OPTIONS = [
  { key: 'free', label: 'Free' },
  { key: 'basic', label: 'Basic' },
  { key: 'solo', label: 'Solo' },
  { key: 'pro', label: 'Pro' },
];
const OVERRIDE_KEY_TO_DISPLAY = { free: 'Free', basic: 'Basic', solo: 'Solo', pro: 'Pro' };

// Google mark next to the email when the account was created with Google,
// reusing the exact pattern (and SVG) AdminDashboard uses.
function SignUpMethodIcon({ method }) {
  if (typeof method !== 'string' || !method.includes('google')) return null;
  const label = method.includes('password') ? 'Google + password' : 'Signed up with Google';
  return (
    <span className="aero-users-signup-method" title={label} aria-label={label}>
      <GoogleGLogo size={12} />
    </span>
  );
}

const PAGE_SIZE = 100;

function AeroUsers() {
  const { isAdmin } = useOutletContext();
  const db = firebase.firestore();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  // Admin controls (Stage 4): the current plan-override key on the open user
  // (null when none), plus busy flags and a status message for each control.
  const [overrideKey, setOverrideKey] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [controlMsg, setControlMsg] = useState(null);

  const adminUid = auth.currentUser?.uid || null;

  // Live-load the full user collection once. Admin-only per Firestore rules
  // (list is gated by isAdmin()), so a non-admin read fails permission-denied
  // and is surfaced as an error state rather than a blank spinner.
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const snap = await db.collection('user').get();
      setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading users:', err);
      setLoadError((err && err.code) || 'unknown');
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const rows = users
      .filter((u) => {
        if (!query) return true;
        return (
          (u.name || '').toLowerCase().includes(query) ||
          (u.email || '').toLowerCase().includes(query)
        );
      })
      .sort((a, b) => {
        // Sensible default: signup date, newest first. Rows missing the field
        // sink to the bottom.
        const ta = sortTime(a.signUpDate);
        const tb = sortTime(b.signUpDate);
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return tb - ta;
      });
    return rows;
  }, [users, query]);

  // Reset the window when the search changes so results start from the top.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query]);

  const visible = filtered.slice(0, visibleCount);

  const openDetail = useCallback(async (row) => {
    setSelected(row);
    setDetail({ loading: true, html: [], figma: [], tests: [] });
    setPlanInfo(null);
    setControlMsg(null);
    // Seed the override control from the user-doc field already loaded in the
    // list (planOverride = { plan, setBy, setAt }).
    setOverrideKey(
      row.planOverride && typeof row.planOverride.plan === 'string'
        ? row.planOverride.plan.toLowerCase()
        : null
    );
    try {
      const [items, plan] = await Promise.all([
        readUserItems(db, row.uid),
        readUserPlan(db, row.uid).catch(() => ({ plan: 'Free', planStatus: 'none', priceId: null })),
      ]);
      setDetail({ loading: false, ...items });
      setPlanInfo(plan);
    } catch (err) {
      console.error('Error loading user detail:', err);
      setDetail({ loading: false, html: [], figma: [], tests: [], error: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDetail = useCallback(() => {
    setSelected(null);
    setDetail(null);
    setPlanInfo(null);
    setOverrideKey(null);
    setControlMsg(null);
  }, []);

  // Persist the override key locally on both the open user and its list row, so
  // reopening reflects the change without a full reload.
  const stampOverrideLocally = useCallback((uid, keyOrNull) => {
    const patch = keyOrNull
      ? { planOverride: { plan: keyOrNull, setBy: adminUid } }
      : { planOverride: null };
    setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, ...patch } : u)));
    setSelected((prev) => (prev && prev.uid === uid ? { ...prev, ...patch } : prev));
  }, [adminUid]);

  // Set or clear the plan override via the admin-only callable.
  const applyPlanOverride = useCallback(async (keyOrNull) => {
    if (!selected || planBusy) return;
    setPlanBusy(true);
    setControlMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'setUserPlanOverride');
      await fn({ uid: selected.uid, plan: keyOrNull });
      setOverrideKey(keyOrNull);
      stampOverrideLocally(selected.uid, keyOrNull);
      setControlMsg({
        ok: true,
        text: keyOrNull
          ? `Plan override set to ${OVERRIDE_KEY_TO_DISPLAY[keyOrNull]}.`
          : 'Plan override cleared.',
      });
    } catch (err) {
      console.error('Error setting plan override:', err);
      setControlMsg({ ok: false, text: 'Could not update the plan override. Are you an admin?' });
    } finally {
      setPlanBusy(false);
    }
  }, [selected, planBusy, stampOverrideLocally]);

  // Grant or revoke my (the admin's) access across all of this user's sites.
  const applyShare = useCallback(async (action) => {
    if (!selected || shareBusy) return;
    setShareBusy(true);
    setControlMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'setUserSitesShare');
      const res = await fn({ uid: selected.uid, action });
      // Refresh the items so the share state (and any Shared badges) reflect
      // the change.
      const items = await readUserItems(db, selected.uid).catch(() => null);
      if (items) setDetail((prev) => ({ ...(prev || {}), loading: false, ...items }));
      setControlMsg({
        ok: true,
        text: action === 'grant'
          ? `Added you to ${res?.data?.updated ?? 'all'} of this user’s sites.`
          : `Removed you from this user’s sites.`,
      });
    } catch (err) {
      console.error('Error updating site share:', err);
      setControlMsg({ ok: false, text: 'Could not update site sharing. Are you an admin?' });
    } finally {
      setShareBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, shareBusy]);

  // Redirect non-admins, mirroring AdminDashboard's gate. isAdmin is threaded
  // down from AeroLayout via the outlet context.
  if (isAdmin === false) {
    return <Navigate to="/user-testing" replace />;
  }

  // Subscription-derived (real) plan, and the effective plan an override
  // produces. When an override is active the badge shows the override plan and
  // the real subscription plan is shown alongside for context.
  const subscriptionPlan = planInfo?.plan || 'Free';
  const effectivePlan = overrideKey ? OVERRIDE_KEY_TO_DISPLAY[overrideKey] : subscriptionPlan;

  // Share state across the open user's sites: how many of their url docs (all
  // groups) currently include this admin's uid in sharedWith.
  const allSiteDocs = detail && !detail.loading
    ? [...(detail.html || []), ...(detail.figma || []), ...(detail.tests || [])]
    : [];
  const sharedWithMeCount = adminUid
    ? allSiteDocs.filter((s) => Array.isArray(s.sharedWith) && s.sharedWith.includes(adminUid)).length
    : 0;

  return (
    <div className="aero-main">
      <Helmet>
        <title>Users | Aero</title>
      </Helmet>

      {/* Header */}
      <div className="aero-page-header">
        <div>
          <h1 className="aero-page-title">Users</h1>
          <p className="aero-page-sub">
            Every Aero + Figmafolio account. Click a user to see their plan,
            sites and user tests.
          </p>
        </div>
      </div>

      {/* Search */}
      {!loading && !loadError && users.length > 0 && (
        <div className="aero-filter-row">
          <span className="aero-users-count">
            {filtered.length.toLocaleString()} user{filtered.length !== 1 ? 's' : ''}
          </span>
          <div style={{ marginLeft: 'auto' }}>
            <label className="aero-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>
        </div>
      )}

      {loading ? (
        <div className="aero-test-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ut-skeleton" style={{ height: 56, borderRadius: 10 }} />
          ))}
        </div>
      ) : loadError ? (
        <div className="aero-empty">
          <h3>Couldn’t load users</h3>
          <p>
            {loadError === 'permission-denied'
              ? 'This account does not have admin access to the user list.'
              : `Something went wrong (${loadError}).`}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="aero-empty">
          <h3>No users found</h3>
          <p>{search ? `No users match "${search}"` : 'No users yet.'}</p>
        </div>
      ) : (
        <>
          <div className="aero-users-table" role="table">
            <div className="aero-users-row aero-users-head" role="row">
              <span role="columnheader">Name</span>
              <span role="columnheader">Email</span>
              <span role="columnheader">Product</span>
              <span role="columnheader">Signed up</span>
              <span role="columnheader">Last active</span>
            </div>
            {visible.map((u) => (
              <div
                key={u.uid}
                className="aero-users-row aero-users-body-row"
                role="row"
                tabIndex={0}
                onClick={() => openDetail(u)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetail(u);
                  }
                }}
              >
                <span className="aero-users-name" role="cell">{u.name || '—'}</span>
                <span className="aero-users-email" role="cell">
                  {u.email || '—'}
                  <SignUpMethodIcon method={u.signUpMethod} />
                </span>
                <span role="cell">
                  {u.signUpProduct ? (
                    <span className="aero-tag aero-users-product">{u.signUpProduct}</span>
                  ) : '—'}
                </span>
                <span className="aero-users-muted" role="cell">{fmtFriendlyDate(u.signUpDate)}</span>
                <span className="aero-users-muted" role="cell">{fmtRelative(u.lastActiveAt)}</span>
              </div>
            ))}
          </div>
          {visibleCount < filtered.length && (
            <div className="aero-users-loadmore">
              <button
                type="button"
                className="aero-btn aero-btn-secondary"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              >
                Load more ({(filtered.length - visibleCount).toLocaleString()} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selected &&
        ReactDOM.createPortal(
          <div className="ut-modal-root aero-modal-backdrop ut-theme" onClick={closeDetail}>
            <div
              className="aero-modal aero-users-detail-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aero-modal-head">
                <div className="aero-users-detail-head">
                  <h2>{selected.name || selected.email || 'User'}</h2>
                  <div className="aero-users-detail-sub">
                    <span>{selected.email || '—'}</span>
                    <SignUpMethodIcon method={selected.signUpMethod} />
                    <span className={planTagClass(effectivePlan)}>{effectivePlan}</span>
                    {overrideKey && (
                      <span className="aero-tag aero-tag-amber" title={`Real subscription: ${subscriptionPlan}`}>
                        Override
                      </span>
                    )}
                    <span className="aero-users-muted">
                      · Last active {fmtRelative(selected.lastActiveAt)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="aero-btn aero-btn-ghost aero-btn-icon"
                  onClick={closeDetail}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="aero-modal-body aero-users-detail-body">
                {/* Admin controls */}
                <div className="aero-users-controls">
                  {/* Plan override */}
                  <div className="aero-users-control">
                    <div className="aero-users-control-label">Plan override</div>
                    <p className="aero-users-control-note">
                      {overrideKey
                        ? `Forced to ${OVERRIDE_KEY_TO_DISPLAY[overrideKey]} (real subscription: ${subscriptionPlan}). This is real entitlement.`
                        : `No override — the real subscription plan (${subscriptionPlan}) applies.`}
                    </p>
                    <div className="aero-users-control-actions">
                      {OVERRIDE_OPTIONS.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          className={`aero-btn aero-btn-sm ${overrideKey === opt.key ? 'aero-btn-primary' : 'aero-btn-secondary'}`}
                          disabled={planBusy}
                          onClick={() => applyPlanOverride(opt.key)}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="aero-btn aero-btn-sm aero-btn-ghost"
                        disabled={planBusy || !overrideKey}
                        onClick={() => applyPlanOverride(null)}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Share all sites */}
                  <div className="aero-users-control">
                    <div className="aero-users-control-label">Site access</div>
                    <p className="aero-users-control-note">
                      {detail?.loading
                        ? 'Loading this user’s sites…'
                        : allSiteDocs.length === 0
                          ? 'This user has no sites to share.'
                          : sharedWithMeCount > 0
                            ? `You have access to ${sharedWithMeCount} of ${allSiteDocs.length} of this user’s sites.`
                            : `You don’t have access to any of this user’s ${allSiteDocs.length} sites.`}
                    </p>
                    <div className="aero-users-control-actions">
                      <button
                        type="button"
                        className="aero-btn aero-btn-sm aero-btn-secondary"
                        disabled={shareBusy || detail?.loading || allSiteDocs.length === 0}
                        onClick={() => applyShare('grant')}
                      >
                        {shareBusy ? 'Working…' : 'Add me to all sites'}
                      </button>
                      <button
                        type="button"
                        className="aero-btn aero-btn-sm aero-btn-ghost"
                        disabled={shareBusy || detail?.loading || sharedWithMeCount === 0}
                        onClick={() => applyShare('revoke')}
                      >
                        Remove me
                      </button>
                    </div>
                  </div>

                  {controlMsg && (
                    <p className={`aero-users-control-msg${controlMsg.ok ? '' : ' error'}`}>
                      {controlMsg.text}
                    </p>
                  )}
                </div>

                {detail?.loading ? (
                  <p className="aero-users-muted">Loading…</p>
                ) : detail?.error ? (
                  <p className="aero-users-muted">Couldn’t load this user’s items.</p>
                ) : (
                  <>
                    <DetailGroup label="HTML sites" items={detail.html} />
                    <DetailGroup label="Figma sites" items={detail.figma} />
                    <TestsGroup tests={detail.tests} ownerUid={selected.uid} />
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// A sites group (html / figma): title with count + a linked row per site.
function DetailGroup({ label, items }) {
  const list = items || [];
  return (
    <div className="aero-users-group">
      <h3 className="aero-users-group-title">{label} ({list.length})</h3>
      {list.length === 0 ? (
        <p className="aero-users-muted aero-users-group-empty">None</p>
      ) : (
        list.map((s) => {
          const url = buildSiteUrl({
            generatedUrl: s.generatedUrl,
            type: s.type,
            customDomain: s.customDomain,
            brand: inferBrand(s),
          });
          const host = url.replace(/^https:\/\//, '');
          return (
            <div key={s.key} className="aero-users-item">
              <span className="aero-users-item-title">{s.title || 'Untitled'}</span>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="aero-users-item-link"
              >
                {host}
              </a>
            </div>
          );
        })
      )}
    </div>
  );
}

// The user-tests group: each test lists its sessions, deep-linking recorded
// ones into /session-review exactly like AdminDashboard.
function TestsGroup({ tests, ownerUid }) {
  const list = tests || [];
  return (
    <div className="aero-users-group">
      <h3 className="aero-users-group-title">User tests ({list.length})</h3>
      {list.length === 0 ? (
        <p className="aero-users-muted aero-users-group-empty">None</p>
      ) : (
        list.map((t) => (
          <div key={t.key} className="aero-users-item aero-users-item-test">
            <div className="aero-users-item-head">
              <span className="aero-users-item-title">{t.title || 'Untitled'}</span>
              <span className="aero-users-muted">
                {t.sessions?.length || 0} session{(t.sessions?.length || 0) !== 1 ? 's' : ''}
              </span>
            </div>
            {t.sessions?.length > 0 && (
              <div className="aero-users-session-links">
                {t.sessions.map((s, i) => {
                  const label = s.startedAt ? fmtFriendlyDate(s.startedAt) : `Session ${i + 1}`;
                  return s.hasMedia ? (
                    <a
                      key={s.id}
                      href={`/users/${ownerUid}/tests/${t.key}/sessions/${s.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aero-users-session-link"
                      title="View recording + transcript"
                    >
                      ▶ {label}
                    </a>
                  ) : (
                    <span
                      key={s.id}
                      className="aero-users-session-link aero-users-session-link-empty"
                      title="This session has no recording"
                    >
                      {label} · no recording
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default AeroUsers;
